import { Bell, CheckCheck, Inbox } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Skeleton } from '@/components/ui'
import {
  useMarcarNotificacaoLida,
  useMarcarTodasLidas,
  useNotificacoes,
  useUnreadNotificationsCount,
} from '@/features/notificacoes/hooks/useNotificacoes'
import type { Notificacao } from '@/features/notificacoes/types/notificacao'
import { notificationTargetPath } from '@/features/notificacoes/utils/notificationTarget'
import { cn } from '@/utils/cn'
import { formatDateTime } from '@/utils/formatters'

const PAGE_SIZE = 8
const PANEL_WIDTH = 320
const VIEWPORT_MARGIN = 16

function targetPath(notificacao: Notificacao): string | null {
  if (!notificacao.referenciaIdPublico) return null
  return notificationTargetPath(notificacao.tipoNotificacao, notificacao.referenciaIdPublico)
}

/**
 * Sino de notificações do Topbar — abre um painel (não uma página) com as
 * notificações recentes, portalizado em `document.body` (mesma técnica do
 * `Menu`) para não ficar preso pelo layout do Topbar.
 */
export function NotificationBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; right: number; width: number } | null>(
    null,
  )
  const anchorRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const { data: count } = useUnreadNotificationsCount()
  const { data, isLoading } = useNotificacoes({ page: 1, pageSize: PAGE_SIZE }, { enabled: open })
  const marcarLida = useMarcarNotificacaoLida()
  const marcarTodasLidas = useMarcarTodasLidas()

  const hasUnread = !!count && count > 0
  const items = data?.items ?? []

  useLayoutEffect(() => {
    if (!open) return
    const rect = anchorRef.current?.getBoundingClientRect()
    if (!rect) return
    // Alinha o painel ao sino, mas sem deixar vazar pelas laterais: em telas estreitas
    // o sino fica longe da borda direita e o painel alinhado a ele sairia pela esquerda.
    const viewport = window.innerWidth
    const width = Math.min(PANEL_WIDTH, viewport - 2 * VIEWPORT_MARGIN)
    const maxRight = viewport - VIEWPORT_MARGIN - width
    const right = Math.min(Math.max(viewport - rect.right, VIEWPORT_MARGIN), maxRight)
    setPosition({ top: rect.bottom + 8, right, width })
  }, [open])

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    // Rolar a própria lista de notificações não deve fechar o painel.
    const onScroll = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) return
      close()
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const handleClick = (notificacao: Notificacao) => {
    if (!notificacao.lida) marcarLida.mutate(notificacao.id)
    setOpen(false)
    const path = targetPath(notificacao)
    if (path) navigate(path)
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={hasUnread ? `Notificações — ${count} não lidas` : 'Notificações'}
        aria-expanded={open}
        className="relative grid size-9 place-items-center rounded-lg text-fg-muted hover:bg-surface-2"
      >
        <Bell className="size-5" />
        {hasUnread && (
          <span className="absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-critical px-1 text-[0.65rem] font-semibold leading-4 text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label="Notificações"
            style={{ top: position.top, right: position.right, width: position.width }}
            className="pc-animate-modal fixed z-40 overflow-hidden rounded-xl border border-line bg-surface shadow-lg"
          >
            <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
              <p className="text-sm font-semibold text-fg">Notificações</p>
              <button
                type="button"
                onClick={() => marcarTodasLidas.mutate()}
                disabled={!hasUnread || marcarTodasLidas.isPending}
                className="flex shrink-0 items-center gap-1 text-xs font-medium text-brand hover:underline disabled:pointer-events-none disabled:opacity-40"
              >
                <CheckCheck className="size-3.5" />
                Marcar todas como lidas
              </button>
            </div>

            <div className="max-h-[min(24rem,calc(100dvh-9rem))] overflow-y-auto overscroll-contain">
              {isLoading ? (
                <div className="space-y-2 p-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                  <Inbox className="size-6 text-fg-subtle" />
                  <p className="text-sm text-fg-muted">Nenhuma notificação por aqui.</p>
                </div>
              ) : (
                <ul className="divide-y divide-line">
                  {items.map((notificacao) => (
                    <li key={notificacao.id}>
                      <button
                        type="button"
                        onClick={() => handleClick(notificacao)}
                        className={cn(
                          'flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-surface-2',
                          !notificacao.lida && 'bg-brand/5',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-1.5 size-1.5 shrink-0 rounded-full',
                            notificacao.lida ? 'bg-transparent' : 'bg-brand',
                          )}
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-fg">
                            {notificacao.titulo}
                          </p>
                          <p className="line-clamp-2 text-xs text-fg-muted">
                            {notificacao.mensagem}
                          </p>
                          <p className="mt-0.5 text-[0.7rem] text-fg-subtle">
                            {formatDateTime(notificacao.dataCriacao)}
                          </p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
